// On-device storage for rounds, so scoring works with no signal. Every tap is
// written here first; the sync engine sends it to the API when it can.
// Plain IndexedDB, no library: two small object stores.

import type { Course, Scorecard } from "@union/contracts";

export interface LocalScore {
  strokes: number;
  /** When it was entered on this device (ISO). Decides which write wins. */
  recordedAt: string;
}

export interface LocalRound {
  roundId: string;
  clubSlug: string;
  course: Course;
  startedAt: string;
  scores: Record<number, LocalScore>;
  /** recordedAt of each hole as the server last confirmed it. */
  confirmed: Record<number, string>;
}

/** Holes whose latest entry the server hasn't confirmed yet. */
export function pendingHoles(round: LocalRound): number[] {
  return Object.entries(round.scores)
    .filter(([hole, score]) => round.confirmed[Number(hole)] !== score.recordedAt)
    .map(([hole]) => Number(hole));
}

const DB_NAME = "union-scoring";
let opening: Promise<IDBDatabase> | undefined;

function db(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore("rounds", { keyPath: "roundId" });
      req.result.createObjectStore("courses", { keyPath: "clubSlug" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return opening;
}

async function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>) {
  const database = await db();
  return new Promise<T>((resolve, reject) => {
    const req = run(database.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export const getRound = (roundId: string) =>
  tx<LocalRound | undefined>("rounds", "readonly", (s) => s.get(roundId));

export const putRound = (round: LocalRound) => tx("rounds", "readwrite", (s) => s.put(round));

export async function listRounds(clubSlug: string): Promise<LocalRound[]> {
  const all = await tx<LocalRound[]>("rounds", "readonly", (s) => s.getAll());
  return all.filter((r) => r.clubSlug === clubSlug).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

/** Courses are cached so a round can be started with no signal. */
export const cacheCourses = (clubSlug: string, courses: Course[]) =>
  tx("courses", "readwrite", (s) => s.put({ clubSlug, courses }));

export async function cachedCourses(clubSlug: string): Promise<Course[] | undefined> {
  const row = await tx<{ courses: Course[] } | undefined>("courses", "readonly", (s) => s.get(clubSlug));
  return row?.courses;
}

/** Records one tap. Never waits on the network. */
export async function recordScore(roundId: string, hole: number, strokes: number): Promise<LocalRound> {
  const round = await getRound(roundId);
  if (!round) throw new Error(`No local round ${roundId}`);
  round.scores[hole] = { strokes, recordedAt: new Date().toISOString() };
  await putRound(round);
  return round;
}

/**
 * Takes the server's scorecard after a sync: marks holes confirmed, and adopts
 * the server's value where it's newer (e.g. entered on another device).
 */
export async function applyServerCard(card: Scorecard): Promise<LocalRound | undefined> {
  const round = await getRound(card.roundId);
  if (!round) return undefined;
  for (const s of card.scores) {
    const local = round.scores[s.hole];
    if (!local || new Date(s.recordedAt) >= new Date(local.recordedAt)) {
      round.scores[s.hole] = { strokes: s.strokes, recordedAt: s.recordedAt };
      round.confirmed[s.hole] = s.recordedAt;
    }
  }
  await putRound(round);
  return round;
}
